import { Button } from "@bcc/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bcc/components/ui/dropdown-menu";
import {
  useRoasterProfile,
  type RoasterProfileType,
} from "@bcc/contexts/roaster-profile-context";
import { Check, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import { setRoasterProfileSelectorTestId } from "./roaster-profile-selector.utils";

const profiles = [
  {
    type: "BLP" as RoasterProfileType,
    name: "BLP Roaster",
    description: "Full access to all features",
  },
  {
    type: "SS" as RoasterProfileType,
    name: "SS Roaster",
    description: "Limited access (Orders hidden)",
  },
  {
    type: "BC OG" as RoasterProfileType,
    name: "BC OG Roaster",
    description: "Orders visible, BLP features disabled",
  },
];

interface RoasterProfileSelectorProps {
  testId?: string;
}

export function RoasterProfileSelector({
  testId,
}: RoasterProfileSelectorProps) {
  const { profileType, setProfileType } = useRoasterProfile();
  const { t } = useTranslation();

  const currentProfile = profiles.find((p) => p.type === profileType);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          testId={testId ?? setRoasterProfileSelectorTestId("trigger")}
          variant="outline"
          size="sm"
          className="gap-2 bg-transparent"
        >
          <UserCog className="h-4 w-4" />
          <span className="hidden sm:inline">{currentProfile?.name}</span>
          <span className="sm:hidden">{currentProfile?.type}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        testId={setRoasterProfileSelectorTestId("content")}
        align="end"
        className="w-64"
      >
        <DropdownMenuLabel>
          {t("layout.roasterProfileSelector.roasterProfile")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={profileType}
          onValueChange={(value) => setProfileType(value as RoasterProfileType)}
        >
          {profiles.map((profile) => (
            <DropdownMenuRadioItem
              key={profile.type}
              value={profile.type}
              className="flex-col items-start gap-1 py-3"
              data-testid={setRoasterProfileSelectorTestId(
                `option-${profile.type.toLowerCase().replace(" ", "-")}`,
              )}
            >
              <div className="flex w-full items-center justify-between">
                <span className="font-medium">{profile.name}</span>
                {profileType === profile.type && (
                  <Check className="h-4 w-4 text-accent-strong" />
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {profile.description}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
